<template>
	<div id="wrapper">
		<div class="header">
			<img src="../statics/img/logo.svg" v-if="!isChPage" />
			<img src="/themes/fc-schaffhausen/logo.png" v-else />
			<h2>Check-In</h2>
		</div>
		<!-- scanner -->
		<div v-if="!isDone" class="scanner" :class="{ 'scanner--resolved': isQrCodeResolved, 'scanner--invalid': isInvalidResult }">
			<div class="scanner__inner">
				<!-- corners -->
				<div class="scanner__corner scanner__corner--tl"></div>
				<div class="scanner__corner scanner__corner--tr"></div>
				<div class="scanner__corner scanner__corner--bl"></div>
				<div class="scanner__corner scanner__corner--br"></div>

				<!-- video -->
				<div v-if="isLoading" class="three-bounce">
					<div class="one"></div>
					<div class="two"></div>
					<div class="three"></div>
				</div>
				<div v-else>
					<video id="qr-video" muted playsinline class="scanner__camera"></video>
				</div>
			</div>

			<div class="scanner__input">
				<input id="scanner-input" v-model="qrCode" type="text" placeholder="Code manuell eingeben" @keydown.enter="scan(qrCode, true)" autocomplete="off" />
				<i class="fad fa-arrow-circle-right" :class="{ 'show': isValidQrCode(qrCode) }" @click="scan(qrCode, true)"></i>
			</div>

			<span v-if="isInvalidResult" class="validation-message">Gescannter Code ist ungültig</span>
		</div>

		<!-- result -->
		<template v-else>
			<div class="btn" @click="newScan()">Neuer Scan ({{ countDown }})</div>
			<div :class="scanResultCssClass">
				<h2>
					<i class="fad fa-ticket-alt"></i>
					{{ scanResult.Data.Code }}
				</h2>
				<ul class="list">
					<!-- status -->
					<template v-if="scanResult.Message">
						<li>
							<span>Status</span>
							<span>
								<small class="d-block">{{ scanResult.Message }}</small>
							</span>
						</li>
					</template>
				</ul>

				<!-- products -->
				<ul v-if="scanResult.Data.Products && scanResult.Data.Products.length" class="list">
					<li v-for="(product, index) in scanResult.Data.Products" :key="index">
						<span>{{ product.Name }}</span>
						<span>{{ product.OccupanyCount }} Personen</span>
					</li>
				</ul>
				<!-- validity -->
				<ul v-if="usedTicketCount" class="list">
					<li v-for="(product, index) in scanResult.Data.Products" :key="index">
							<span>Anzahl der genutzten Tickets</span>
							<span>{{scanResult.Data.NumberOfScans}}/{{product.Validity.For.Value}}</span>
					</li>
				</ul>
				<!-- payment -->
				<ul v-if="scanResult.Data.Payment" class="list">
					<li>
						<span>Art</span>
						<span>{{ paymentMethod }}</span>
					</li>
					<li>
						<span>Betrag</span>
						<span>{{ scanResult.Data.Payment.Total }}€</span>
					</li>
					<!-- <li>
						<span>Status</span>
						<span>{{ PaymentStatus }}  </span>
					</li> -->
				</ul>

				<!-- occupancy -->
				<ul v-if="scanResult.Data.Date" class="list">
					<li>
						<span>Personen</span>
						<span>{{ scanResult.Data.TotalOccupancy }}</span>
					</li>
					<li>
						<span>Buchende Person</span>
						<span>{{ scanResult.Data.MainPerson }}</span>
					</li>
					<li class="d21-persons" v-if="scanResult.Data.Persons">
						<div>Registrierte Personen</div>
						<div v-for="(person, pIndex) in scanResult.Data.Persons" :key="'person_' + pIndex">
							{{ pIndex + 1 }}. {{ person.Givenname }} {{ person.Lastname }}<br />
							<img :src="getPhotoID(person)" />
						</div>
					</li>
				</ul>

				<!-- valid dates -->
				<ul v-if="scanResult.Data.MultiDates && !scanResult.Data.IsSeasonal" class="list">
					<template v-for="(date, index) in scanResult.Data.MultiDates">
						<li :key="'date_' + index">
							<span>Datum</span>
							<span>{{ date.From | date("ddd, DD.MM.YYYY") }}</span>
						</li>
						<li :key="'time_' + index">
							<span>Uhrzeit</span>
							<span>{{ getTime(date.From )}} - {{ getTime(date.Until )}} Uhr</span>
						</li>
					</template>
				</ul>
			</div>

			<!-- Logs -->
			<div v-if="isDone && scanResult && log.length" class="log">
				<h2>
					<i class="fad fa-file-alt"></i>
					<span>Ticket Log</span>
				</h2>
				<ul class="list">
					<li v-for="(logItem, index) in log" :key="index">
						<span style="font-size: 10px;">{{ logItem }}</span>
					</li>
				</ul>
			</div>
		</template>

		<!-- progress bars -->
		<template>
			<div class="info-box" v-if="hasGuestCount">
				<table>
					<tr>
						<th>
							<p>Eingecheckt:</p>
						</th>
						<th>
							<p>Ausgecheckt:</p>
						</th>
					</tr>
					<tr>
						<!-- checked in -->
						<th>
							<radial-progress-bar :diameter="100" :completed-steps="guestCount.CheckedIn" :total-steps="guestCount.Total" :stop-color="'#ccc'" :start-color="'#fff'" :stroke-width="5">
								<span class="number__top">{{guestCount.CheckedIn}} </span>
								<span class="number__bottom">{{guestCount.Total}} </span>
							</radial-progress-bar>
						</th>
						<!-- checked out -->
						<th>
							<radial-progress-bar :diameter="100" :completed-steps="guestCount.CheckedOut" :total-steps="guestCount.TotalCheckedIn" :stop-color="'#ccc'" :start-color="'#fff'" :stroke-width="5">
								<span class="number__top">{{guestCount.CheckedOut}} </span>
								<span class="number__bottom">{{guestCount.TotalArrived}} </span>
							</radial-progress-bar>
						</th>
					</tr>
				</table>
			</div>
		</template>
	</div>
</template>

<script>
	import scanMixin from '../mixins/_scan.js';
	import RadialProgressBar from "vue-radial-progress";

	export default {
		name: "check-in",
		mixins: [scanMixin],
		components: {
			RadialProgressBar
		},
		data() {
			return {
				guestCountInterval: null
			}
		},
		computed: {
			usedTicketCount(){
				return this.scanResult.Data.Products && this.scanResult.Data.Products.some(p => p.Validity && p.Validity.Type === "Multi");
			},
			log() {
				return this.scanResult.Data.Log != null ?
					this.scanResult.Data.Log.slice(Math.max(this.scanResult.Data.Log.length - 3, 1)) : [];
			},
			paymentMethod() {
				if (!this.scanResult.Data.Payment) return null;
				switch (this.scanResult.Data.Payment.Method.toLowerCase()) {
					case "cash":
						return "An der Kasse";
					case "creditcard":
						return "Kreditkarte";
					case "free":
						return "Kostenlos";
					default:
						return this.scanResult.Data.Payment.Method;
				}
			},

			hasGuestCount() {
				return this.guestCount != null;
			}
		},
		methods: {
			getPhotoID(person) {
				return `https://api-default.tocc.dirs21.de/v1/cdn/images/ticketing/${this.scanResult.Data._oid}/${this.scanResult.Data.Channel}/${this.scanResult.Data._uuid}/${person.PhotoID.Url}?w=320&o=Landscape`;
			},
			onGuestCount(countResult) {
				this.guestCount = countResult;
			},
			getGuestCount() {
				const request = {
					method: "GET"
				};

				var directory = this.$route.params.directory || "public";
				fetch(`/api/checkedInStatus/${directory}`, request).then(response => {
					response.json().then(result => {
						if (result) {
							this.onGuestCount(result);
						}
					});
				});
			},
			setGuestCountInterval() {
				this.guestCountInterval = setInterval(() => {
					this.getGuestCount();
				}, 20000);
			}
		},
		mounted() {
			//
			this.getGuestCount();
			this.setGuestCountInterval();
		},
		beforeDestroy() {
			clearInterval(this.guestCountInterval);
		}
	};
</script>